/**
 * THE D18 ANCHOR MATHS. ONE IMPLEMENTATION, TWO CALLERS.
 *
 * `adr/0008`, verbatim: "**One anchor implementation, two callers.** The editor and the
 * moderation worker must produce identical anchors; same package, same unit (code points), one test
 * suite proving both." `12-build-plan.md` makes that a milestone step - "the content-model
 * round-trip, as property tests and not one example ... an anchor that differs by one side is a
 * moderation verdict pointing at the wrong sentence" - and `02-nfr.md` is why the file is in
 * this package rather than in `src/`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ★★ THE UNIT IS THE CODE POINT AND THE BASE STRING IS `body`. BOTH HALVES ARE RULED.
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * `adr/0008` (Q31b): an issue anchors to "block id + in-block offsets **measured in Unicode code
 * points over the block's stored plain text - never over any markup form of it** + a
 * quote-with-context fallback; if both fail the issue degrades to 'unanchored'".
 * `research/19-block-content-anchors-research.md`: "Every offset column is named `*_cp`, is
 * `integer`, is **0-based** and **half-open** `[start_cp, end_cp)`", which is the W3C
 * `TextPositionSelector` convention D18 adopts. `04-schema.md` calls `body` "the one
 * authoritative string. EVERY offset in the system is measured over it, in code points."
 *
 * ★ SO THIS FILE MEASURES THE STRING AS IT IS GIVEN AND NEVER NORMALISES IT, which is the one place
 * it deliberately differs from `text-measure.ts` next door. `countTextLength` normalises to NFC
 * before counting, because D21's title and quote BOUNDS must not depend on which path wrote the row.
 * An OFFSET is the opposite case: it indexes the stored string, and `ck_awb_body_nfc` /
 * `ck_avb_body_nfc` (live) already guarantee that string is NFC. Re-normalising here would move
 * every offset relative to the string the database holds - "two strings means two coordinate
 * systems", which is exactly the objection that settled `WC-CR-01` (`03-worksheet.md`). The two
 * functions agree on every NFC string, which is asserted rather than assumed.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ★★ THE PARAGRAPH TILING, AND THE DECISION THAT THE SIGNED-OFF CORPUS DOES NOT STATE IT
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * `research/19-block-content-anchors-research.md` gives four invariants and says "All four
 * are checkable in SQL, and all four are asserted by the round-trip property tests":
 *
 *     para[0].start_cp = 0
 *     para[i].end_cp + 1 = para[i+1].start_cp        -- the U+000A sits at para[i].end_cp
 *     para[last].end_cp = char_length(body)
 *     substring(body from para[i].end_cp + 1 for 1) = E'\n'   for every i < last
 *
 * `04-schema.md` carries the TABLE and not the tiling: its only paragraph rule is `ck_avpara_range
 * CHECK (start_cp >= 0 AND end_cp >= start_cp)` (`:3195`). **DECISION RECORDED - the corpus above
 * the research layer is silent and this package takes the note's side.** Three reasons, in order of
 * weight: note 19 is the note that owns the block and `04-schema.md`'s pair is derived from it; a
 * document whose paragraphs leave a gap has text belonging to no paragraph and therefore NO markdown
 * form at all, so `codec.ts` could not be lossless (`research/19:730`, test 14); and the separator
 * being a real `\n` in `body` is what makes "an author who presses Enter twice keeps the blank line"
 * (`authors-api.yaml`) a fact about the stored string rather than about a row.
 *
 * C11: no clock is read here and no instant is taken. Nothing about an offset is time-dependent.
 */
import type { BlockKind, InlineMarkKind, ParagraphStyle } from './blocks';
import { DEFAULT_PARAGRAPH_STYLE, MAX_LIST_LEVEL, isListStyle } from './blocks';

/**
 * The paragraph separator inside a block's `body`.
 *
 * ONE `\n` AND NEVER `\r\n`: `ck_awb_body_nocr CHECK (body IS NULL OR strpos(body, E'\r') = 0)` is
 * live on both block tables (`04-schema.md`), so a carriage return cannot reach a stored body
 * and this file never has to guess which of two separators an offset was measured over.
 */
export const PARAGRAPH_SEPARATOR = '\n';

/**
 * How much context a quote carries on each side.
 *
 * `ari_context_budget CHECK ((quote_prefix IS NULL OR char_length(quote_prefix) <= 32) AND
 * (quote_suffix IS NULL OR char_length(quote_suffix) <= 32))` - `04-schema.md`, and
 * `char_length` counts code points, which is the unit this whole file works in. Hypothes.is stores
 * "exact + 32 characters of context on each side" and is where the number came from
 * (`research/18-editor-review-flow-data-research.md`).
 */
export const QUOTE_CONTEXT_MAX_CP = 32;

/** One paragraph's range over its block's text, in code points, half-open. */
export interface ParagraphRange {
  readonly paraIndex: number;
  readonly startCp: number;
  readonly endCp: number;
}

/** What the client says ABOUT a paragraph that its text cannot say: its style and its nesting. */
export interface ParagraphStyling {
  readonly style: ParagraphStyle;
  readonly listLevel: number;
}

/** A whole `article_version_block_para` / `article_working_block_para` row, less its `block_key`. */
export interface ParagraphRow extends ParagraphRange, ParagraphStyling {}

/** A whole `*_block_mark` row, less its `block_key`. */
export interface MarkRow {
  readonly startCp: number;
  readonly endCp: number;
  readonly kind: InlineMarkKind;
  readonly linkHref: string | null;
}

/** The W3C `TextQuoteSelector`: `exact`, and the snippets immediately before and after it. */
export interface QuoteWithContext {
  /** `article_review_issue.quote_exact` - `TextQuoteSelector.exact`. */
  readonly quoteExact: string;
  /** `<= 32` code points before, or `null` when the span opens the text. */
  readonly quotePrefix: string | null;
  /** `<= 32` code points after, or `null` when the span closes it. */
  readonly quoteSuffix: string | null;
}

/**
 * A resolved span anchor: the nine `article_review_issue` anchor columns for `scope = 'span'`, less
 * the `article_version_id` the caller holds.
 *
 * It is the same shape `ReviewFindingAnchor`'s span branch declares in `src/` - deliberately, so the
 * moderation worker can spread it - and the reason the version id is NOT here is `WC-CR-14`'s cost:
 * "a `block_id` alone identifies a block's *lineage*, not a row", so the id travels with the version
 * on the ROW and never inside the maths (`04-schema.md`).
 */
export interface SpanAnchor extends QuoteWithContext {
  readonly blockId: string;
  readonly startCp: number;
  readonly endCp: number;
}

/** How a stored span resolved against a changed text - `review_anchor_state`'s first two codes. */
export type AnchorState = 'position' | 'quote';

/** Where a stored span landed in a text that has moved on. */
export interface Relocation {
  readonly startCp: number;
  readonly endCp: number;
  readonly state: AnchorState;
}

/** One block, as far as anchoring is concerned. */
export interface AnchorBlock {
  readonly blockId: string;
  readonly kind: BlockKind;
  /** The block's `body`. `null` for a kind that has none - a divider. */
  readonly text: string | null;
  /** The client's styles, positionally per paragraph. The RANGES are never the client's. */
  readonly paragraphs?: readonly ParagraphStyling[] | undefined;
  readonly marks?: readonly MarkRow[] | undefined;
}

/** One block's computed rows. Deterministic in its input and in nothing else. */
export interface BlockAnchors {
  readonly blockId: string;
  readonly kind: BlockKind;
  readonly text: string | null;
  readonly paragraphs: readonly ParagraphRow[];
  readonly marks: readonly MarkRow[];
}

// ------------------------------------------------------------------------------------------------
// Code points
// ------------------------------------------------------------------------------------------------

/**
 * A text as its code points.
 *
 * Spreading a string iterates it by code point - the same construction `text-measure.ts`
 * argues for at length, and `"😄".length === 2` (`research/19:843`) is the bug it avoids.
 */
export function codePointsOf(text: string): readonly string[] {
  return [...text];
}

/** How many code points, over the string AS GIVEN. See the header for why nothing is normalised. */
export function codePointLength(text: string): number {
  return codePointsOf(text).length;
}

/**
 * The half-open slice `[startCp, endCp)`.
 *
 * `research/19:438`: "The 1-based ↔ 0-based conversion is written down once and only once:
 * `substring(body from start_cp + 1 for end_cp - start_cp)`. Off-by-one here is the classic bug in
 * this design." This function is that conversion's JavaScript twin and the only place in this
 * package that cuts a stored string.
 */
export function sliceCp(text: string, startCp: number, endCp: number): string {
  return codePointsOf(text).slice(startCp, endCp).join('');
}

/** Every code-point index at which `needle` occurs in `haystack`, left to right. */
function findAllCp(haystack: readonly string[], needle: readonly string[]): readonly number[] {
  if (needle.length === 0 || needle.length > haystack.length) {
    return [];
  }
  const hits: number[] = [];
  for (let start = 0; start + needle.length <= haystack.length; start += 1) {
    let matched = true;
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (haystack[start + offset] !== needle[offset]) {
        matched = false;
        break;
      }
    }
    if (matched) {
      hits.push(start);
    }
  }
  return hits;
}

// ------------------------------------------------------------------------------------------------
// Paragraph rows: derived from the text, never taken from the client
// ------------------------------------------------------------------------------------------------

/**
 * THE TILING. One range per paragraph, derived from the separators in the text and from nothing
 * else.
 *
 * AN EMPTY TEXT IS ONE EMPTY PARAGRAPH, NOT NO PARAGRAPHS. The invariants require
 * `para[0].start_cp = 0` and `para[last].end_cp = char_length(body)`, and both hold for the single
 * range `[0, 0)`. It also keeps `codec.ts` honest: "no paragraphs" and "one empty paragraph" would
 * otherwise encode to the same empty string.
 */
export function paragraphRangesFor(text: string): readonly ParagraphRange[] {
  const cps = codePointsOf(text);
  const ranges: ParagraphRange[] = [];
  let start = 0;
  for (let index = 0; index <= cps.length; index += 1) {
    if (index === cps.length || cps[index] === PARAGRAPH_SEPARATOR) {
      ranges.push({ paraIndex: ranges.length, startCp: start, endCp: index });
      start = index + 1;
    }
  }
  return ranges;
}

/**
 * The paragraph ROWS: this file's ranges, carrying the caller's styles.
 *
 * ★ THE SPLIT IS THE WHOLE POINT. A range is a fact about the text and is recomputed; a style is a
 * fact the text cannot carry and is the author's. A styling list shorter than the derived tiling
 * leaves the remaining paragraphs at `p` / level 0 rather than refusing, because that is exactly the
 * client that sent no `paragraphs` at all - which the contract permits (`Paragraph` is not in
 * `TextBlock`'s `required` list, `authors-api.yaml`).
 */
export function paragraphRowsFor(
  text: string,
  styles: readonly ParagraphStyling[] = [],
): readonly ParagraphRow[] {
  return paragraphRangesFor(text).map((range) => {
    const styling = styles[range.paraIndex];
    return {
      ...range,
      style: styling?.style ?? DEFAULT_PARAGRAPH_STYLE,
      listLevel: styling?.listLevel ?? 0,
    };
  });
}

// ------------------------------------------------------------------------------------------------
// The refusals: what the server says when the client's rows are not the server's
// ------------------------------------------------------------------------------------------------

/** A named refusal, so a caller can raise it at the member it belongs to. */
export interface RowRefusal {
  /** The stable name. It is IN the message too, because the pipe forwards messages and not names. */
  readonly name: string;
  readonly detail: string;
  /** Where to hang the issue, relative to the block. */
  readonly path: readonly (string | number)[];
}

/** The client's paragraph ranges are not the ones the block's own text produces. */
export const PARAGRAPH_ROWS_DISAGREE = 'PARAGRAPH_ROWS_DISAGREE';
/** A paragraph carries a nesting depth and its style is not a list style. */
export const LIST_LEVEL_ON_NON_LIST_STYLE = 'LIST_LEVEL_ON_NON_LIST_STYLE';
/** A mark's offsets do not lie inside the block's text. */
export const MARK_OUTSIDE_BLOCK_TEXT = 'MARK_OUTSIDE_BLOCK_TEXT';

function describeRange(range: ParagraphRange): string {
  return `#${String(range.paraIndex)} [${String(range.startCp)},${String(range.endCp)})`;
}

/**
 * Do the client's paragraph rows say what the text says? `null` when they do.
 *
 * ★ THE SERVER'S COMPUTATION IS THE AUTHORITY AND THE CLIENT'S ROWS ARE CHECKED AGAINST IT, not the
 * other way round - the DECISION `article-editor.schema.ts` records, and the reason this function
 * answers a refusal rather than a repaired list: a save that silently moved an author's paragraph
 * boundaries would be the "editor that deletes their spacing on save", which
 * `authors-api.yaml` calls data loss and asks to be reported.
 *
 * ONLY THE RANGES ARE COMPARED. `style` and `listLevel` are not derivable from plain text and are
 * taken as sent - checked here only for the one rule the contract states about them.
 */
export function refuseParagraphRows(
  text: string,
  rows: readonly ParagraphRow[],
): RowRefusal | null {
  const derived = paragraphRangesFor(text);
  if (rows.length !== derived.length) {
    return {
      name: PARAGRAPH_ROWS_DISAGREE,
      detail:
        `the block's text has ${String(derived.length)} paragraph(s) and ` +
        `${String(rows.length)} row(s) were sent; a paragraph boundary is one U+000A in the body ` +
        '(the tiling of research/19-block-content-anchors-research.md:157-164)',
      path: ['paragraphs'],
    };
  }
  for (const [index, row] of rows.entries()) {
    const expected = derived[index];
    if (expected === undefined) {
      continue;
    }
    if (
      row.paraIndex !== expected.paraIndex ||
      row.startCp !== expected.startCp ||
      row.endCp !== expected.endCp
    ) {
      return {
        name: PARAGRAPH_ROWS_DISAGREE,
        detail:
          `paragraph row ${String(index)} was sent as ${describeRange(row)} and the block's own ` +
          `text makes it ${describeRange(expected)}; offsets are code points, half-open, and the ` +
          'separator between two paragraphs is one U+000A that belongs to neither of them',
        path: ['paragraphs', index],
      };
    }
    if (row.listLevel > 0 && !isListStyle(row.style)) {
      return {
        name: LIST_LEVEL_ON_NON_LIST_STYLE,
        detail:
          `paragraph row ${String(index)} is styled '${row.style}' and carries listLevel ` +
          `${String(row.listLevel)}; only a list style may carry more than zero ` +
          '(authors-api.yaml:4239-4243, paragraph_style.is_list)',
        path: ['paragraphs', index, 'listLevel'],
      };
    }
    if (row.listLevel > MAX_LIST_LEVEL) {
      return {
        name: LIST_LEVEL_ON_NON_LIST_STYLE,
        detail:
          `paragraph row ${String(index)} carries listLevel ${String(row.listLevel)} and ` +
          `ck_awpara_level admits 0..${String(MAX_LIST_LEVEL)}`,
        path: ['paragraphs', index, 'listLevel'],
      };
    }
  }
  return null;
}

/**
 * Do the client's marks lie inside the block's text? `null` when they do.
 *
 * EVERY INTEGER IN `[0, length]` IS A CODE-POINT BOUNDARY, WHICH IS THE POINT OF THE UNIT: the only
 * way a mark can fail to be on one is by running past the end. That is the condition
 * `trg_awmark_in_range` raises on - "offsets %..% fall outside the block text (% code points)",
 * `04-schema.md` - and the contract already names it as a refusal of this request rather
 * than a fault: "an offset that does not lie inside its block" (`authors-api.yaml`). Left
 * to the trigger it is a `raise_exception` three layers away, which the author reads as a 500.
 */
export function refuseMarkRows(text: string, marks: readonly MarkRow[]): RowRefusal | null {
  const length = codePointLength(text);
  for (const [index, mark] of marks.entries()) {
    if (mark.startCp < 0 || mark.endCp <= mark.startCp || mark.endCp > length) {
      return {
        name: MARK_OUTSIDE_BLOCK_TEXT,
        detail:
          `mark ${String(index)} spans [${String(mark.startCp)},${String(mark.endCp)}) and the ` +
          `block's text is ${String(length)} code point(s) long`,
        path: ['marks', index],
      };
    }
  }
  return null;
}

// ------------------------------------------------------------------------------------------------
// Quote with context, and the anchors both callers build from it
// ------------------------------------------------------------------------------------------------

/**
 * Q31b's fallback half: the exact quote plus up to 32 code points either side.
 *
 * `null` RATHER THAN `''` AT AN EDGE, and the column is why: `quote_prefix` is nullable and a
 * zero-length string would be a claim that there IS context and that it is empty. A span that opens
 * the block has no prefix, and the re-anchor below must not go looking for one.
 */
export function quoteWithContext(
  text: string,
  startCp: number,
  endCp: number,
  contextCp: number = QUOTE_CONTEXT_MAX_CP,
): QuoteWithContext {
  const cps = codePointsOf(text);
  const start = Math.max(0, Math.min(startCp, cps.length));
  const end = Math.max(start, Math.min(endCp, cps.length));
  const prefixFrom = Math.max(0, start - contextCp);
  const suffixTo = Math.min(cps.length, end + contextCp);
  return {
    quoteExact: cps.slice(start, end).join(''),
    quotePrefix: start === 0 ? null : cps.slice(prefixFrom, start).join(''),
    quoteSuffix: end === cps.length ? null : cps.slice(end, suffixTo).join(''),
  };
}

/** One span of one block, as the nine anchor columns hold it. */
export function spanAnchorFor(
  blockId: string,
  text: string,
  startCp: number,
  endCp: number,
): SpanAnchor {
  return { blockId, startCp, endCp, ...quoteWithContext(text, startCp, endCp) };
}

/**
 * ★★ THE FUNCTION BOTH CALLERS RUN. Every block's rows, computed from its own text.
 *
 * It is deterministic in its argument and reads nothing else - no clock, no database, no locale - so
 * the editor and the moderation worker cannot disagree about an anchor unless they disagree about
 * the block, which is the property `12-build-plan.md` asks for and
 * `test/shared/content-model/anchor-agreement.spec.ts` asserts of the two real call sites.
 *
 * ONLY A TEXT BLOCK GETS PARAGRAPH ROWS - see `blockParagraphs` in `blocks.ts` for the contract
 * reason. A divider has no text and therefore no rows of either kind.
 */
export function anchorsFor(blocks: readonly AnchorBlock[]): readonly BlockAnchors[] {
  return blocks.map((block) => ({
    blockId: block.blockId,
    kind: block.kind,
    text: block.text,
    paragraphs:
      block.kind === 'text' && block.text !== null
        ? paragraphRowsFor(block.text, block.paragraphs ?? [])
        : [],
    marks: block.marks ?? [],
  }));
}

// ------------------------------------------------------------------------------------------------
// Re-anchoring
// ------------------------------------------------------------------------------------------------

/** What a stored anchor needs to carry to be relocatable. */
export interface StoredSpan extends QuoteWithContext {
  readonly startCp: number;
  readonly endCp: number;
}

/**
 * ★ THE ORDERED STRATEGY: position, then context, then the quote alone, then `null`.
 *
 * `research/18-editor-review-flow-data-research.md` gives the order and Hypothes.is is where
 * it comes from (`:329`): "four strategies in order: range → position → context-first fuzzy
 * (prefix+suffix) → selector-only fuzzy", and - the half that matters more - the client "uses the
 * quote as a check on the other strategies": every non-media match passes through
 * `maybeAssertQuote()`, which throws when the text at the offsets is not the quote. That is why step
 * 1 below is not "the offsets still fit" but "the offsets still hold the SAME TEXT".
 *
 * ★ A SECOND HIT IS A `null`, NOT A GUESS. `review_anchor_state` has a code for "lost its anchor"
 * and none for "probably here"; `adr/0008` requires that "a failed re-anchor degrades to
 * 'unanchored' and is never lost". An ambiguous quote silently resolved to its first occurrence is
 * the one outcome D18 rules out - a finding pointing at different words.
 *
 * NO FUZZY TAIL. Hypothes.is's fourth strategy is a diff-match-patch Bitap search, which is a
 * dependency this package may not take (`text-measure.spec.ts` records the same rule for
 * `fast-check`). The honest answer is `unanchored`, which the model already has a value for.
 */
export function relocate(span: StoredSpan, newText: string): Relocation | null {
  if (span.quoteExact === '') {
    return null;
  }
  const cps = codePointsOf(newText);
  const exact = codePointsOf(span.quoteExact);

  // 1. POSITION - the cheap one, and only when the text there is still the quote.
  if (
    span.endCp <= cps.length &&
    span.endCp - span.startCp === exact.length &&
    cps.slice(span.startCp, span.endCp).join('') === span.quoteExact
  ) {
    return { startCp: span.startCp, endCp: span.endCp, state: 'position' };
  }

  // 2. CONTEXT - prefix ‖ exact ‖ suffix, and only if it occurs exactly once.
  const prefix = codePointsOf(span.quotePrefix ?? '');
  const suffix = codePointsOf(span.quoteSuffix ?? '');
  if (prefix.length > 0 || suffix.length > 0) {
    const withContext = [...prefix, ...exact, ...suffix];
    const hits = findAllCp(cps, withContext);
    if (hits.length === 1 && hits[0] !== undefined) {
      const start = hits[0] + prefix.length;
      return { startCp: start, endCp: start + exact.length, state: 'quote' };
    }
  }

  // 3. THE QUOTE ALONE - same uniqueness rule.
  const bare = findAllCp(cps, exact);
  if (bare.length === 1 && bare[0] !== undefined) {
    return { startCp: bare[0], endCp: bare[0] + exact.length, state: 'quote' };
  }
  return null;
}

/**
 * Find a quotation in a document and answer the anchor for it, or `null`.
 *
 * ★ UNIQUE ACROSS THE WHOLE DOCUMENT, not per block. A sentence that occurs in two blocks is not
 * anchorable by quotation, and answering the first of them would point a finding at the wrong
 * paragraph - the same refusal `relocate` makes for the same reason. The caller then records an
 * `article`- or `block`-scoped anchor, which is a truthful smaller claim.
 */
export function locateQuote(blocks: readonly AnchorBlock[], quote: string): SpanAnchor | null {
  if (quote === '') {
    return null;
  }
  const needle = codePointsOf(quote);
  let found: SpanAnchor | null = null;
  for (const block of blocks) {
    if (block.text === null) {
      continue;
    }
    const hits = findAllCp(codePointsOf(block.text), needle);
    if (hits.length === 0) {
      continue;
    }
    const first = hits[0];
    if (hits.length > 1 || found !== null || first === undefined) {
      return null;
    }
    found = spanAnchorFor(block.blockId, block.text, first, first + needle.length);
  }
  return found;
}

/** What `carryMarks` answers: the marks that made it, and how many did not. */
export interface CarriedMarks {
  readonly marks: readonly MarkRow[];
  readonly dropped: number;
}

/**
 * ★ CARRY A BLOCK'S MARKS ONTO A NEW TEXT OF THE SAME BLOCK, dropping the ones that cannot be found.
 *
 * WHAT IT IS FOR. `translation-intake.writer.ts` calls it: the ranges do not survive a translation -
 * "the target text is a different length, so every `start_cp` in the source is meaningless in the
 * target" (`research/19:304`) - and the corpus's answer is that the wire carries markdown which is
 * "re-parsed on intake into fresh paragraph and mark rows" (`04-schema.md`). Until the
 * submit sends `codec.ts`'s markdown, the honest middle is this: a mark whose exact quotation is
 * still there (a name, a product, a Latin phrase, a number) keeps its formatting; one that is not is
 * DROPPED and COUNTED, never guessed at. `03-worksheet.md` is the sentence that makes a
 * difference legitimate: "Paragraph count and mark set may legitimately differ from the source".
 *
 * IT IS THE SAME `relocate` THE EDITOR USES, so "the mark moved" and "the finding moved" are one
 * question asked twice and not two implementations.
 */
export function carryMarks(
  sourceText: string,
  marks: readonly MarkRow[],
  targetText: string,
): CarriedMarks {
  const carried: MarkRow[] = [];
  let dropped = 0;
  for (const mark of marks) {
    const span = { ...quoteWithContext(sourceText, mark.startCp, mark.endCp), ...mark };
    const landed = relocate(span, targetText);
    if (landed === null) {
      dropped += 1;
      continue;
    }
    carried.push({
      startCp: landed.startCp,
      endCp: landed.endCp,
      kind: mark.kind,
      linkHref: mark.linkHref,
    });
  }
  return { marks: carried, dropped };
}
